import { useCallback, useEffect, useState } from "react";

import { listThemes, type ListThemesQuery } from "@/api/themes.api";
import type { ThemeListItem } from "@/types/themes";

export const THEMES_DEFAULT_PAGE_SIZE = 25;

export interface UseThemesListOptions {
  readonly categoryId?: string;
  readonly status?: string;
  readonly pageSize?: number;
}

export interface UseThemesListResult {
  readonly themes: readonly ThemeListItem[];
  readonly loading: boolean;
  readonly error: Error | null;
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly totalPages: number;
  readonly hasNextPage: boolean;
  setPage(page: number): void;
  refetch(): void;
}

/**
 * CR-111: paginated list of themes for the ThemesListPage grid.
 *
 * The hook is intentionally minimal — we only need page-state + the
 * CR-111 additive preview fields from the backend. Filter changes
 * (categoryId / status / pageSize) automatically reset to page 1 so
 * a stale page offset never survives a filter change. Each fetch is
 * aborted on cleanup so rapid filter toggles can't race.
 */
export function useThemesList(
  options: UseThemesListOptions = {},
): UseThemesListResult {
  const { categoryId, status, pageSize = THEMES_DEFAULT_PAGE_SIZE } = options;

  const [themes, setThemes] = useState<readonly ThemeListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [page, setPageState] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [attempt, setAttempt] = useState(0);

  // Reset to page 1 whenever a filter (not the page) changes.
  useEffect(() => {
    setPageState(1);
  }, [categoryId, status, pageSize]);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);

    const query: ListThemesQuery = {
      page,
      page_size: pageSize,
      ...(categoryId ? { category_id: categoryId } : {}),
      ...(status ? { status } : {}),
    };

    listThemes(query, ctrl.signal)
      .then((resp) => {
        if (cancelled || ctrl.signal.aborted) return;
        setThemes(resp.items);
        setTotal(resp.total);
        setTotalPages(resp.total_pages);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled || ctrl.signal.aborted) return;
        setError(err);
        setThemes([]);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [page, pageSize, categoryId, status, attempt]);

  const setPage = useCallback((next: number) => {
    setPageState(Math.max(1, next));
  }, []);

  const refetch = useCallback(() => {
    setAttempt((a) => a + 1);
  }, []);

  return {
    themes,
    loading,
    error,
    page,
    pageSize,
    total,
    totalPages,
    hasNextPage: page < totalPages,
    setPage,
    refetch,
  };
}
